import { TireSeason } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { commonTexts } from 'noddi-util';
import { resolveTireSeasonInput, TIRE_SEASON_STYLE, type TireSeasonInput } from './tireSeasonStyle';

type Size = 'sm' | 'md' | 'lg';

// Same scale as the worker app's chip: sm for table cells and list rows, md for cards and detail headers,
// lg as the icon box of a season option.
const SIZE = {
  sm: { box: 'size-[22px]', chip: 'h-[22px] gap-1 pr-2 pl-1.5', radius: 'rounded-md', icon: 14, text: 'text-xs' },
  md: { box: 'size-7', chip: 'h-7 gap-1.5 pr-2.5 pl-2', radius: 'rounded-lg', icon: 16, text: 'text-sm' },
  lg: { box: 'size-10', chip: 'h-10 gap-2 pr-3 pl-2.5', radius: 'rounded-xl', icon: 22, text: 'text-base' }
} as const satisfies Record<Size, { box: string; chip: string; radius: string; icon: number; text: string }>;

export interface NavioTireSeasonChipTranslations {
  summerTires: string;
  winterTires: string;
  allSeasonTires: string;
}

interface Props {
  season: TireSeasonInput | null | undefined;
  size?: Size;
  /** Drops the label and makes the box square; the label stays the accessible name and tooltip. */
  iconOnly?: boolean;
  /** With `iconOnly`: a missing season draws an empty outlined box instead of nothing, so plates in a column stay aligned. */
  placeholder?: boolean;
  className?: string;
  translations?: NavioTireSeasonChipTranslations;
}

/**
 * The one way web shows a tire season: a tinted box with the season's icon, labelled as a chip or icon-only in
 * front of a plate. Renders nothing for a missing or unknown season.
 */
export const NavioTireSeasonChip = ({
  season,
  size = 'md',
  iconOnly = false,
  placeholder = false,
  className,
  translations = commonTexts.seasonBadge
}: Props) => {
  const resolved = resolveTireSeasonInput(season);
  const sizing = SIZE[size];
  if (!resolved) {
    return iconOnly && placeholder ? (
      <span aria-hidden className={cn('shrink-0 border border-border', sizing.box, sizing.radius, className)} />
    ) : null;
  }
  const { icon, tintClassName } = TIRE_SEASON_STYLE[resolved];
  const label = seasonLabel(resolved, translations);

  return (
    <span
      role={iconOnly ? 'img' : undefined}
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap',
        iconOnly ? sizing.box : sizing.chip,
        sizing.radius,
        sizing.text,
        tintClassName,
        className
      )}
    >
      <NoddiIcon name={icon} size={sizing.icon} strokeWidth={2} />
      {iconOnly ? null : label}
    </span>
  );
};

const seasonLabel = (season: TireSeason, translations: NavioTireSeasonChipTranslations): string => {
  if (season === TireSeason.SUMMER) {
    return translations.summerTires;
  }
  if (season === TireSeason.WINTER) {
    return translations.winterTires;
  }
  return translations.allSeasonTires;
};
